/**
 * API del panel del club atada a UN club. Cada pantalla recibe el club de los
 * datos que muestra (ClubScopeContext) y toda lectura/escritura sale con ESE
 * club_id; nunca con un «club actual» global que pudo cambiar mientras tanto.
 */
import { createContext, useContext, useMemo } from "react";

import { api, runWithClubScope } from "./api";
import { t123MembershipApi } from "./t123MembershipApi";

type AnyFn = (...args: never[]) => unknown;

function scoped<T extends object>(target: () => T, clubId: number | null): T {
  return new Proxy({} as T, {
    get(_obj, prop) {
      const value = (target() as Record<PropertyKey, unknown>)[prop];
      if (typeof value !== "function") return value;
      return (...args: unknown[]) => runWithClubScope(clubId, () => (value as AnyFn)(...(args as never[])));
    },
  });
}

export function scopedClubApi(clubId: number | null): typeof api.clubs {
  return scoped(() => api.clubs, clubId);
}

export function scopedT123Api(clubId: number | null): typeof t123MembershipApi {
  return scoped(() => t123MembershipApi, clubId);
}

/** Club (confirmado) de los datos que se están mostrando. */
export const ClubScopeContext = createContext<number | null>(null);

export function useClubApi(): typeof api.clubs {
  const clubId = useContext(ClubScopeContext);
  return useMemo(() => scopedClubApi(clubId), [clubId]);
}

export function useT123ClubApi(): typeof t123MembershipApi {
  const clubId = useContext(ClubScopeContext);
  return useMemo(() => scopedT123Api(clubId), [clubId]);
}
